import { useCallback, useEffect, useRef, useState } from "react";
import type { SupplementCatalogResponse } from "@ferrivia/api-contract";
import { useApi } from "@ferrivia/auth-web";

export type SupplementCatalogState =
  | { status: "loading" }
  | { status: "error"; error: string }
  | { status: "success"; data: SupplementCatalogResponse };

export interface UseSupplementCatalogResult {
  state: SupplementCatalogState;
  refetch: () => void;
}

// Shape copied from useCatalog.ts -- fetch on mount plus on-demand refetch,
// no poll, no react-query. All 31 rows arrive in one response and there is
// no search endpoint, so filtering the list is client-side by design.
export function useSupplementCatalog({ enabled = true }: { enabled?: boolean } = {}): UseSupplementCatalogResult {
  const api = useApi();
  const [state, setState] = useState<SupplementCatalogState>({ status: "loading" });
  const cancelledRef = useRef(false);

  const fetchCatalog = useCallback(() => {
    api.supplements
      .getCatalog()
      .then((data) => {
        if (!cancelledRef.current) {
          setState({ status: "success", data });
        }
      })
      .catch((err: unknown) => {
        if (!cancelledRef.current) {
          setState({
            status: "error",
            error: err instanceof Error ? err.message : "Failed to load supplement catalog",
          });
        }
      });
  }, [api]);

  useEffect(() => {
    if (!enabled) return;
    cancelledRef.current = false;
    fetchCatalog();
    return () => {
      cancelledRef.current = true;
    };
  }, [enabled, fetchCatalog]);

  return { state, refetch: fetchCatalog };
}
